import React, { FunctionComponent } from 'react';
import { useQuery, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { H1, H2 } from '@app/shop/components/typography';
import useGTMTracking from '@app/shop/hooks/useGTMTracking';

import { LoadMoreButton } from './OrderHistory.styles';
import messages from './OrderHistory.messages';
import OrderHistoryItems from './OrderHistoryItems';
import {
  OrderHistoryQuery,
  OrderHistoryQuery_magento2_customer_globalOrders,
} from './__generated__/OrderHistoryQuery';

const ORDER_HISTORY_QUERY = gql`
  query OrderHistoryQuery($currentPage: Int) {
    magento2 {
      customer {
        nativeId
        globalOrders(pageSize: 30, currentPage: $currentPage) {
          totalCount
          pageInfo {
            currentPage
          }
          items {
            id
            number
            orderDate
            total {
              grandTotal {
                value
                currency
              }
            }
            status
            items {
              productName
              quantityOrdered
            }
          }
        }
      }
    }
  }
`;

const readOrders = (
  data?: OrderHistoryQuery
): OrderHistoryQuery_magento2_customer_globalOrders | null =>
  data?.magento2?.customer?.globalOrders ?? null;

const OrderHistory: FunctionComponent = () => {
  const { data, fetchMore, loading } = useQuery<OrderHistoryQuery>(
    ORDER_HISTORY_QUERY,
    {
      variables: {
        currentPage: 1,
      },
    }
  );
  const orders = readOrders(data);
  const orderItems = readOrders(data)?.items;
  const hasNextPage = orderItems?.length !== orders?.totalCount;
  const loadMore = () =>
    fetchMore({
      variables: {
        currentPage: (readOrders(data)?.pageInfo?.currentPage ?? 1) + 1,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (
          hasNextPage &&
          fetchMoreResult?.magento2?.customer?.globalOrders?.items
        ) {
          const paginatedOrderItems = [
            ...(readOrders(previousResult)?.items ?? [null]),
            ...(readOrders(fetchMoreResult)?.items ?? [null]),
          ];

          fetchMoreResult.magento2.customer.globalOrders.items = paginatedOrderItems;
          return fetchMoreResult;
        }

        return previousResult;
      },
    });

  useGTMTracking({});

  if (loading) return null;

  return (
    <>
      <H1>
        <FormattedMessage {...messages.title} />
      </H1>

      <H2>
        <FormattedMessage {...messages.subTitle} />
      </H2>

      {orderItems?.length ? (
        <>
          <OrderHistoryItems items={orderItems} />
          {hasNextPage && (
            <LoadMoreButton wide type="primary" onClick={loadMore}>
              <FormattedMessage {...messages.loadMore} />
            </LoadMoreButton>
          )}
        </>
      ) : (
        <p>
          <FormattedMessage {...messages.empty} />
        </p>
      )}
    </>
  );
};

export default OrderHistory;
